import map from "lodash/fp/map";
import flow from "lodash/fp/flow";
import isNil from "lodash/fp/isNil";
import cloneDeep from "lodash/fp/cloneDeep";
import toString from "lodash/fp/toString";
import moment from "moment";
import capitalize from "lodash/fp/capitalize";
import writtenNumber from "written-number";
import identity from "lodash/fp/identity";
import constants from "../../core/constants";

class CorporateInvoicePageTransform {
  constructor(Lists) {
    "ngInject";
    this.Lists = Lists.all;
    this.summaryTableBody = [];
    this.summaryTableFooter = [];
    this.detailsTableBody = [];
    this.detailsTableFooter = [];
    this.infoTableBody = [];
    this.amountTableBody = [];
    this.invoiceTotalInWords = "";

    this.invoiceItemsFields = [
      { title: "Date", name: "createdAt" },
      { title: "Amount", name: "amountWithCurrency" },
      { title: "Type", name: "type" },
      { title: "Contract Version", name: "contract_version" },
      { title: "Active", name: "active" },
      { title: "Operator", name: "operator" },
    ];

    this.filters = [
      {
        name: "createdAt",
        title: "Select date",
        icon: "date_range",
        isDate: true,
      },
      {
        name: "type",
        title: "Type",
        icon: "airport_shuttle",
        list: "corporateInvoiceItemType",
        multiple: true,
      },
      { name: "active", title: "Active", icon: "person", list: "active" },
    ];
    this.additionInvoiceTypes = [
      {
        title: "Gas Variance Charge",
        name: "gas_variance_charge",
        extraFields: [
          {
            title: "Ride Id",
            name: "ride",
          },
        ],
      },
      {
        title: "Extra Kilometers Charge",
        name: "extra_kilometers_charge",
        extraFields: [
          {
            title: "Ride Id",
            name: "ride",
          },
        ],
      },
      {
        title: "Extra Time Charge",
        name: "extra_time_charge",
        extraFields: [
          {
            title: "Ride Id",
            name: "ride",
          },
        ],
      },
      {
        title: "Late Communication Charge",
        name: "late_communication_charge",
        extraFields: [
          {
            title: "Reason",
            name: "reason",
          },
        ],
      },
      {
        title: "Late Payment Charge",
        name: "late_payment_charge",
        extraFields: [
          {
            title: "Invoice Id",
            name: "late_invoice",
          },
        ],
      },
      {
        title: "Promotion Billing",
        name: "promotion_billing",
        extraFields: [
          {
            title: "Promo code",
            name: "promotion_code",
            resource: "Promotion",
          },
        ],
      },
      {
        title: "Other Charge",
        name: "other_charge",
        extraFields: [
          {
            title: "Reason",
            name: "reason",
          },
        ],
      },
    ];
    this.deductionInvoiceTypes = [
      {
        title: "Gas Variance Penalty",
        name: "gas_variance_penalty",
        extraFields: [
          {
            title: "Ride Id",
            name: "ride",
          },
        ],
      },
      {
        title: "Alternative Transport Compensation Penalty",
        name: "alternative_transport_compensation_penalty",
        extraFields: [
          {
            title: "Ride Id",
            name: "ride",
          },
        ],
      },
      {
        title: "Captain Attitude Penalty",
        name: "captain_attitude_penalty",
        extraFields: [
          {
            title: "Ride Id",
            name: "ride",
          },
        ],
      },
      {
        title: "No Show Penalty",
        name: "no_show_penalty",
        extraFields: [
          {
            title: "Ride Id",
            name: "ride",
          },
        ],
      },
      {
        title: "Different Vehicle Penalty",
        name: "different_vehicle_penalty",
        extraFields: [
          {
            title: "Ride Id",
            name: "ride",
          },
        ],
      },
      {
        title: "Different Captain Penalty",
        name: "different_captain_penalty",
        extraFields: [
          {
            title: "Ride Id",
            name: "ride",
          },
        ],
      },
      {
        title: "Different Route Penalty",
        name: "different_route_penalty",
        extraFields: [
          {
            title: "Ride Id",
            name: "ride",
          },
        ],
      },
      {
        title: "Other Penalty",
        name: "other_penalty",
        extraFields: [
          {
            title: "Reason",
            name: "reason",
          },
        ],
      },
    ];
  }

  justify(data) {
    const format = (item) => {
      item = cloneDeep(item);
      switch (item.status) {
        case "in_progress":
          item.status = "In Progress";
          break;
        case "pending":
          item.status = "Pending";
          break;
        case "final":
          item.status = "Final";
          break;
        default:
          break;
      }
      item.start_date_time = moment
        .utc(item.start_date)
        .format(constants.dateTimeFormat);
      item.start_date = moment
        .utc(item.start_date)
        .format(constants.dateFormat);
      item.end_date_time = moment
        .utc(item.end_date)
        .format(constants.dateTimeFormat);
      item.end_date = moment.utc(item.end_date).format(constants.dateFormat);
      item.paid_at = moment(item.paid_at).format(constants.dateTimeFormat);
      item.sub_total = (item.sub_total / constants.currencyBy).toFixed(2);
      this.invoiceSubTotal = item.sub_total;
      item.wht = isNil(item.wht)
        ? "N/A"
        : (item.wht / constants.currencyBy).toFixed(2);
      this.WHTPercentage = isNil(item.corporate)
        ? ""
        : item.corporate.wht_percentage;
      this.WHT = item.wht !== "N/A" ? item.wht : undefined;
      item.total_amount = (item.total_amount / constants.currencyBy).toFixed(2);
      this.invoiceTotalAmount = item.total_amount;
      return item;
    };
    return flow([format, identity])(data);
  }

  justifyInvoiceItems(data) {
    const format = (item) => {
      item = cloneDeep(item);
      item.invoiceStatus =
        isNil(item.invoice) && isNil(item.invoice.status)
          ? ""
          : item.invoice.status;
      item.createdAt = moment(item.createdAt).format(constants.dateTimeFormat);
      item.type = isNil(item.type) ? "" : item.type.name;
      item.contract_version = isNil(item.corporate_contract)
        ? ""
        : "v" + item.corporate_contract.version;
      item.amount = parseFloat((item.amount / constants.currencyBy).toFixed(2));
      item.amountWithCurrency = isNil(item.amount)
        ? " " + item.currency
        : toString(item.amount) + " " + item.invoice.currency;
      this.invoiceCurrency = item.invoice.currency;
      const firstNote =
        !isNil(item.notes) && !isNil(item.notes[0]) ? item.notes[0] : "";
      item.operator =
        !isNil(firstNote) && !isNil(firstNote.operator)
          ? firstNote.operator.name
          : "";
      return item;
    };
    return map(format)(data);
  }

  justifyInvoiceExporting(items, invoice) {
    const corporateName = isNil(invoice.corporate)
      ? ""
      : invoice.corporate.name;
    this.infoTableBody.push(
      [
        {
          content: "Account Name:",
          styles: { fontStyle: "bold", cellWidth: "wrap" },
        },
        corporateName,
        {
          content: "Start Date:",
          styles: { fontStyle: "bold", cellWidth: "wrap" },
        },
        {
          content: invoice.start_date,
          styles: { cellWidth: "wrap" },
        },
      ],
      [
        {
          content: "Invoice Number:",
          styles: { fontStyle: "bold", cellWidth: "wrap" },
        },
        invoice._id,
        {
          content: "End Date:",
          styles: { fontStyle: "bold", cellWidth: "wrap" },
        },
        {
          content: invoice.end_date,
          styles: { cellWidth: "wrap" },
        },
      ]
    );
    let invoiceSummary = {};
    items.forEach((item) => {
      invoiceSummary = {
        ...invoiceSummary,
        [item.type]: {
          count: invoiceSummary[item.type]
            ? invoiceSummary[item.type].count + 1
            : 1,
          amount: invoiceSummary[item.type]
            ? invoiceSummary[item.type].amount + item.amount
            : item.amount,
        },
      };
      const itemRide = item.ride;
      const itemDate = isNil(itemRide)
        ? moment(item.createdAt).format(constants.dateFormat)
        : moment(itemRide.createdAt).format(constants.dateFormat);
      const rideTime = isNil(itemRide)
        ? ""
        : moment(itemRide.createdAt).format(constants.timeFormat);

      const itemRoute =
        isNil(itemRide) || isNil(itemRide.route)
          ? ""
          : itemRide.route.name.length > 50
          ? `${itemRide.route.name.slice(0, 51)}...`
          : itemRide.route.name;

      const itemBusType =
        isNil(itemRide) || isNil(itemRide.bus_type)
          ? ""
          : itemRide.bus_type.name;
      this.detailsTableBody.push([
        { content: itemDate, styles: { cellWidth: "wrap" } },
        { content: rideTime, styles: { cellWidth: "wrap" } },
        itemBusType,
        itemRoute,
        item.type,
        this.justifyTableAmount(item.amount),
      ]);
    });

    this.summaryTableBody = Object.entries(invoiceSummary).map(
      ([key, value]) => {
        return [key, value.count, this.justifyTableAmount(value.amount)];
      }
    );
    this.summaryTableFooter = this.invoiceTablesFooter(2);
    this.detailsTableFooter = this.invoiceTablesFooter(5);

    this.amountTableBody = [
      [
        {
          content: "Amount in words:",
          styles: { fontStyle: "bold", overflow: "visible", cellWidth: "wrap" },
        },
        { content: this.invoiceTotalInWords },
      ],
    ];
  }

  invoiceTablesFooter(colSpan) {
    const tableFooter = [];
    const totalAmountStyles = {
      fontStyle: "bold",
    };
    const paddingStyle = {
      cellPadding: {
        left: 5,
      },
    };

    const cellWidthStyle = {
      cellWidth: "wrap",
    };
    if (!isNil(this.invoiceTotalAmount)) {
      if (isNil(this.WHT)) {
        this.invoiceTotalInWords = this.convertInvoiceAmountToWords(
          this.invoiceTotalAmount
        );
        tableFooter.push(
          [
            {
              content: "Subtotal",
              colSpan,
              styles: paddingStyle,
            },
            {
              content: `${this.invoiceCurrency} ${this.invoiceSubTotal}`,
              styles: cellWidthStyle,
            },
          ],
          [
            {
              content: "Total Amount",
              colSpan,
              styles: { ...totalAmountStyles, ...paddingStyle },
            },
            {
              content: `${this.invoiceCurrency} ${this.invoiceTotalAmount}`,
              styles: { ...totalAmountStyles, ...cellWidthStyle },
            },
          ]
        );
      } else {
        this.invoiceTotalInWords = this.convertInvoiceAmountToWords(
          this.invoiceTotalAmount
        );

        tableFooter.push(
          [
            {
              content: "Subtotal",
              colSpan,
              styles: paddingStyle,
            },
            {
              content: `${this.invoiceCurrency} ${this.invoiceSubTotal}`,
              styles: cellWidthStyle,
            },
          ],
          [
            {
              content: `WHT (${this.WHTPercentage}%)`,
              colSpan,
              styles: paddingStyle,
            },
            {
              content: `${this.invoiceCurrency} ${this.WHT}`,
              styles: cellWidthStyle,
            },
          ],
          [
            {
              content: "Total Amount",
              colSpan,
              styles: { ...totalAmountStyles, ...paddingStyle },
            },
            {
              content: `${this.invoiceCurrency} ${this.invoiceTotalAmount}`,
              styles: { ...totalAmountStyles, ...cellWidthStyle },
            },
          ]
        );
      }
    }
    return tableFooter;
  }

  justifyTableAmount(amount) {
    const styles = { cellWidth: "wrap" };
    let formatAmountCurrency = `${this.invoiceCurrency} ${Math.abs(
      amount
    ).toFixed(2)}`;
    if (amount < 0) {
      formatAmountCurrency = `(${formatAmountCurrency})`;
      styles.textColor = "#f00";
    }
    return { content: formatAmountCurrency, styles };
  }

  convertInvoiceAmountToWords(totalAmount) {
    const isNegativeTotal = Math.floor(totalAmount) < 0;
    const flooredTotal = Math.abs(Math.floor(totalAmount));
    const preAmount = isNegativeTotal
      ? `Negative ${writtenNumber(flooredTotal)}`
      : `${writtenNumber(flooredTotal)}`;
    const regex = /\d+./; // To find decimal numbers;
    const decimal = parseFloat(totalAmount).toFixed(2).replace(regex, "");
    const currency = this.Lists.currencyList.find(
      (currency) => this.invoiceCurrency === currency.name
    );
    // Check if the decimal part isn't zero
    if (parseInt(decimal, 10) !== 0) {
      return `${capitalize(preAmount)} ${currency.title} and ${writtenNumber(
        decimal
      )} ${currency.decimal} only`;
    }
    return `${capitalize(preAmount)} ${currency.title} only`;
  }
}

export default CorporateInvoicePageTransform;



// WEBPACK FOOTER //
// ./src/pages/corporateInvoice/corporateInvoice.transforms.js